import React, { useState } from 'react';
import { useFilters } from '../contexts/FilterContext';
import { Card, Spinner, ErrorBox, Empty } from '../components/ui';
import MultiDropdown from '../components/MultiDropdown';
import { useStoreOptions, SaveBar, useEditableList } from '../components/adminKit';
import AccountingProfilesCard from './admin/AccountingProfilesCard';

// Tenant self-administration, in three independent cards:
//  - User Access (admins only): who can sign in, roles, per-user store scope
//  - Custom Store Groups (admins + owners): admins manage all groups; owners
//    manage groups built from their own stores (chain-wide groups stay
//    admin-only, enforced server-side — the store picker is already scoped)
//  - Accounting Profiles (everyone, incl. bookkeepers): chart-of-accounts
//    mapping per store set, same ownership rule as groups
// DB credentials, hostnames, and Firebase config are deliberately not
// editable (or even visible) here.

// Every role includes the Accounting section; bookkeeper is the one that
// gets NOTHING else.
const ROLES = [
  { id: 'admin', label: 'Admin — every store, every report (incl. Accounting), manages users' },
  { id: 'owner', label: 'Owner — selected stores, every report (incl. Accounting)' },
  { id: 'bookkeeper', label: 'Bookkeeper — selected stores, Accounting section only' },
];

const blankUser = () => ({ email: '', role: 'owner', locations: [] });

function UsersCard({ me }) {
  const storeOptions = useStoreOptions();
  const s = useEditableList('/api/admin/tenant-settings', d => d.users, items => ({ users: items }));
  const [newUser, setNewUser] = useState(blankUser());

  if (s.loadError) return <ErrorBox message={s.loadError} />;
  if (!s.meta) return <Spinner label="Loading user access…" />;

  const users = s.items;
  const setUser = (i, patch) => s.setItems(us => us.map((u, j) => (j === i ? { ...u, ...patch } : u)));

  const addUser = () => {
    const email = newUser.email.trim().toLowerCase();
    if (!email) return;
    if (users.some(u => u.email === email)) {
      s.setSaveError(`${email} is already on the list`);
      return;
    }
    s.setSaveError(null);
    s.setItems(us => [...us, { ...newUser, email }]);
    setNewUser(blankUser());
  };

  return (
    <>
      {!s.meta.editable && (
        <div className="notice"><strong>Read-only:</strong> {s.meta.reason}</div>
      )}
      <fieldset className="bare" disabled={!s.meta.editable || s.saving}>
        <Card
          tour="admin-users"
          title="User Access"
          sub="who can sign in to this dashboard — admins and owners get every report including Accounting (every store vs. their own); a bookkeeper gets only the Accounting section for their stores · changes apply immediately on save"
        >
          <div className="table-wrap">
            <table className="data admin-table">
              <thead>
                <tr>
                  <th className="no-sort">Email</th>
                  <th className="no-sort">Role</th>
                  <th className="no-sort">Stores</th>
                  <th className="no-sort" style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.email}>
                    <td>
                      {u.email}
                      {me?.email?.toLowerCase() === u.email && <span className="hint"> (you)</span>}
                    </td>
                    <td>
                      <select
                        className="field"
                        value={u.role}
                        onChange={e => setUser(i, { role: e.target.value })}
                      >
                        {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                    </td>
                    <td>
                      {u.role === 'admin' ? <span className="hint">All stores</span> : (
                        <MultiDropdown
                          icon="🏪"
                          allLabel="Choose stores…"
                          options={storeOptions}
                          selected={u.locations}
                          onChange={(locations) => setUser(i, { locations })}
                        />
                      )}
                    </td>
                    <td>
                      <button
                        className="btn-ghost"
                        title="Remove user"
                        onClick={() => s.setItems(us => us.filter((_, j) => j !== i))}
                      >✕</button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={4}><Empty>No users yet — anyone signed in to this Firebase project currently has full access. Add the first admin below to lock it down.</Empty></td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="admin-addrow">
            <input
              className="field"
              type="email"
              placeholder="email@example.com"
              value={newUser.email}
              onChange={e => { const email = e.target.value; setNewUser(n => ({ ...n, email })); }}
              onKeyDown={e => e.key === 'Enter' && addUser()}
              style={{ minWidth: 220 }}
            />
            <select
              className="field"
              value={newUser.role}
              onChange={e => { const role = e.target.value; setNewUser(n => ({ ...n, role })); }}
            >
              {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            {newUser.role !== 'admin' && (
              <MultiDropdown
                icon="🏪"
                allLabel="Choose stores…"
                options={storeOptions}
                selected={newUser.locations}
                onChange={(locs) => setNewUser(n => ({ ...n, locations: locs }))}
              />
            )}
            <button className="btn-ghost" onClick={addUser}>+ Add user</button>
          </div>

          {me?.superAdmin && (
            <div className="hint" style={{ marginTop: 8 }}>
              You're signed in as a platform admin — your account isn't on this list and is managed outside this page.
            </div>
          )}

          <SaveBar dirty={s.dirty} saving={s.saving} savedFlash={s.savedFlash} onSave={s.save} onDiscard={s.discard} />
        </Card>
      </fieldset>
      {s.saveError && <ErrorBox message={s.saveError} />}
    </>
  );
}

function GroupsCard({ isAdmin }) {
  const storeOptions = useStoreOptions();
  const s = useEditableList('/api/admin/store-groups', d => d.groups, items => ({ locationGroups: items }));

  if (s.loadError) return <ErrorBox message={s.loadError} />;
  if (!s.meta) return <Spinner label="Loading store groups…" />;

  const groups = s.items;
  const setGroup = (i, patch) => s.setItems(gs => gs.map((g, j) => (j === i ? { ...g, ...patch } : g)));

  return (
    <>
      {!s.meta.editable && (
        <div className="notice"><strong>Read-only:</strong> {s.meta.reason}</div>
      )}
      <fieldset className="bare" disabled={!s.meta.editable || s.saving}>
        <Card
          tour="admin-groups"
          title="Custom Store Groups"
          sub={isAdmin
            ? 'named store sets for the location picker and Group Comparison · city and province groups are automatic'
            : 'your own named store sets, built from your stores · chain-wide groups are managed by head office · city and province groups are automatic'}
        >
          {groups.length === 0 && <Empty>No custom groups yet. City and province groups still appear automatically.</Empty>}
          {groups.map((g, i) => (
            <div className="admin-addrow" key={i}>
              <input
                className="field"
                placeholder="Group name (e.g. Corporate stores)"
                value={g.name}
                onChange={e => setGroup(i, { name: e.target.value })}
                style={{ minWidth: 220 }}
              />
              <MultiDropdown
                icon="🏪"
                allLabel="Choose stores…"
                options={storeOptions}
                selected={g.locations}
                onChange={(locations) => setGroup(i, { locations })}
              />
              <button
                className="btn-ghost"
                title="Remove group"
                onClick={() => s.setItems(gs => gs.filter((_, j) => j !== i))}
              >✕</button>
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <button className="btn-ghost" onClick={() => s.setItems(gs => [...gs, { name: '', locations: [] }])}>
              + Add group
            </button>
          </div>

          <SaveBar dirty={s.dirty} saving={s.saving} savedFlash={s.savedFlash} onSave={s.save} onDiscard={s.discard} />
        </Card>
      </fieldset>
      {s.saveError && <ErrorBox message={s.saveError} />}
    </>
  );
}

export default function AdminPage() {
  const { me } = useFilters();
  if (!me) return <Spinner label="Loading…" />;
  const isAdmin = me.role === 'admin';
  const isBookkeeper = me.role === 'bookkeeper';

  return (
    <div className="grid" style={{ gap: 14 }}>
      {isAdmin && <UsersCard me={me} />}
      {!isBookkeeper && <GroupsCard isAdmin={isAdmin} />}
      <AccountingProfilesCard me={me} />
    </div>
  );
}
